import { cx } from "@emotion/css";
import { Plus } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import routing from "routing";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { FolderItemSkeleton } from "src/components/NewComponents/Skeletons/FolderItemSkeleton";
import ExamView from "src/components/NewComponents/Views/ScoreViews/ExamView";
import { FolderItemType } from "src/components/Folders/types";
import { useGetAllUserExamsInfinite } from "src/hooks/useExams";

interface ModalSelectedItem {
	itemId: string;
	itemType: FolderItemType;
}

interface ExamItem {
	id: string;
	name?: string;
	[key: string]: unknown;
}

interface ExamsFolderTabProps {
	debouncedSearchValue: string;
	selectedItems: ModalSelectedItem[];
	toggleSelection: (item: ExamItem, type: FolderItemType) => void;
	onClose: () => void;
	setIsSearching: (value: boolean) => void;
	folderId: string;
}

export const ExamsFolderTab = ({
	debouncedSearchValue,
	selectedItems,
	toggleSelection,
	onClose,
	setIsSearching,
	folderId,
}: ExamsFolderTabProps) => {
	const { t } = useTranslation();
	const history = useHistory();

	const handleCreate = () => {
		onClose();
		history.push(`${routing.paths.pickExamType}?folderId=${folderId}`);
	};

	const {
		data: examsData,
		fetchNextPage: fetchNextExamPage,
		hasNextPage: hasNextExamPage,
		isFetchingNextPage: isFetchingNextExamPage,
		isFetching: isFetchingExamsData,
		isLoading: isFetchingExams,
	} = useGetAllUserExamsInfinite(
		{ ...(debouncedSearchValue ? { search: debouncedSearchValue } : {}) },
		true,
	);

	useEffect(() => {
		setIsSearching(isFetchingExamsData && !isFetchingExams);
	}, [isFetchingExamsData, isFetchingExams, setIsSearching]);

	const flatExams = useMemo(() => {
		if (!examsData?.pages) return [];
		return examsData.pages.flatMap((page: { data?: ExamItem[] }) => page.data || []);
	}, [examsData?.pages]);

	const renderExamItem = (item: ExamItem) => {
		const isSelected = selectedItems.some(
			(i) => i.itemId === item.id && i.itemType === "exam",
		);

		return (
			<div
				key={item.id as string}
				onClick={() => toggleSelection(item, "exam")}
				className={cx("cursor-pointer position-relative transition-all")}
				data-test-id="folder-modal-exam-item"
				data-exam-id={item.id}
			>
				<div
					className={cx(
						"pointer-events-none border-2 border-solid border-radius-16 radius-16",
					)}
				>
					<ExamView
						hideButton
						row={item}
						className={cx(
							"mb-0 w-100 mx-0",
							isSelected ? "border-color-primary" : "",
						)}
					/>
				</div>
			</div>
		);
	};

	if (isFetchingExams && !flatExams.length) {
		return (
			<div className="folder-modal-scroll">
				<div className="folder-modal-grid">
					{Array.from({ length: 4 }).map((_, i) => (
						<FolderItemSkeleton key={i} />
					))}
				</div>
			</div>
		);
	}

	if (!flatExams.length) {
		return (
			<div className="flex flex-col items-center justify-center py-48 gap-16">
				<span className="text-black-60 font-14">
					{debouncedSearchValue
						? t("folder.noSearchResults", "Brak wyników wyszukiwania")
						: t("folder.noExams", "Nie masz jeszcze żadnych egzaminów")}
				</span>
				{!debouncedSearchValue && (
					<PrimaryButton
						onClick={handleCreate}
						className="flex items-center gap-8"
					>
						<Plus size={16} />
						{t("folder.createExam", "Utwórz egzamin")}
					</PrimaryButton>
				)}
			</div>
		);
	}

	return (
		<div className="folder-modal-scroll">
			<div className="folder-modal-grid">{flatExams.map(renderExamItem)}</div>
			{hasNextExamPage && (
				<div className="flex justify-center mt-24">
					<TextPrimaryButton
						onClick={() => fetchNextExamPage()}
						disabled={isFetchingNextExamPage}
					>
						{isFetchingNextExamPage
							? t("folder.loading", "Ładowanie...")
							: t("folder.loadMore", "Załaduj więcej")}
					</TextPrimaryButton>
				</div>
			)}
		</div>
	);
};
